import classNames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

interface Props {
  children: React.ReactElement
  className?: string
}

enum ShadowPosition {
  none,
  left,
  right,
  leftAndRight,
}

interface TableScrollShadowProps {
  position: ShadowPosition.left | ShadowPosition.right
  offset: number
  className?: string
  isVisible: boolean
}

function TableScrollShadow({ className, position, offset, isVisible }: TableScrollShadowProps) {
  return (
    <div
      className={classNames(
        'pointer-events-none absolute bottom-0 z-20 my-px w-3 border-primary from-gray-900/10 dark:from-gray-900/50',
        className,
        {
          'opacity-100 transition-opacity duration-200': isVisible,
          'opacity-0': !isVisible,
          'border-l bg-gradient-to-r': position === ShadowPosition.left,
          'border-r bg-gradient-to-l': position === ShadowPosition.right,
        }
      )}
      style={position === ShadowPosition.left ? { left: offset } : { right: offset }}
    />
  )
}

export function FinancialsTableStickyColumnWrapper({ children, className }: Props) {
  const stickyRef = React.useRef<HTMLDivElement>(null)
  const [shadowPosition, setShadowPosition] = React.useState(ShadowPosition.none)
  const [firstRowTdWidth, setFirstRowTdWidth] = React.useState(0)

  React.useEffect(() => {
    const stickyElement = stickyRef.current
    if (!stickyElement) return

    const onScroll = throttle(function () {
      const stickyElement = stickyRef.current
      if (!stickyElement) return

      let newShadowPosition = ShadowPosition.none
      const hasScrollSpaceToLeft = stickyElement.scrollLeft > 0
      const hasScrollSpaceToRight = stickyElement.offsetWidth + stickyElement.scrollLeft < stickyElement.scrollWidth
      if (hasScrollSpaceToLeft && hasScrollSpaceToRight) {
        newShadowPosition = ShadowPosition.leftAndRight
      } else if (hasScrollSpaceToLeft) {
        newShadowPosition = ShadowPosition.left
      } else if (hasScrollSpaceToRight) {
        newShadowPosition = ShadowPosition.right
      }

      setShadowPosition(newShadowPosition)
    }, 100)

    onScroll()

    stickyElement.scrollTo({
      left: stickyElement.scrollWidth,
    })

    const firstRowTd = stickyRef.current?.querySelector('tr > td')
    setFirstRowTdWidth(firstRowTd?.clientWidth ?? 0)

    stickyElement.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onScroll)

    return () => {
      stickyElement.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return (
    <div className="relative max-w-full">
      <TableScrollShadow
        isVisible={[ShadowPosition.left, ShadowPosition.leftAndRight].includes(shadowPosition)}
        position={ShadowPosition.left}
        offset={firstRowTdWidth}
        className={className}
      />
      <div className="relative max-w-full overflow-auto whitespace-nowrap" ref={stickyRef}>
        {children}
      </div>
      <TableScrollShadow
        isVisible={[ShadowPosition.right, ShadowPosition.leftAndRight].includes(shadowPosition)}
        position={ShadowPosition.right}
        className={className}
        offset={0}
      />
    </div>
  )
}
